import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { facilityAPI } from '../services/api';
import { Factory, Plus, Edit2, Trash2, BarChart2, X, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'react-toastify';

const FUEL_TYPES = [
  { value: 'natural_gas', label: 'Natural Gas', unit: 'MMBtu' },
  { value: 'diesel', label: 'Diesel / No. 2 Fuel Oil', unit: 'gallons' },
  { value: 'propane', label: 'Propane', unit: 'gallons' },
  { value: 'gasoline', label: 'Gasoline', unit: 'gallons' },
  { value: 'no6_fuel_oil', label: 'No. 6 Fuel Oil / Residual', unit: 'gallons' },
  { value: 'coal', label: 'Coal', unit: 'tons' },
  { value: 'wood_biomass', label: 'Wood / Biomass', unit: 'tons' },
  { value: 'throughput_tons', label: 'Throughput (tons)', unit: 'tons' },
  { value: 'throughput_gallons', label: 'Throughput (gallons)', unit: 'gallons' },
  { value: 'hours_only', label: 'Hours Only (no fuel)', unit: 'N/A' },
  { value: 'other', label: 'Other', unit: 'units' },
];

const SOURCE_TYPES = ['Boiler', 'Emergency Generator', 'Combustion Turbine', 'Process Heater', 'Storage Tank', 'Paint Booth', 'Spray Booth', 'Printing Operation', 'Degreaser', 'Cooling Tower', 'Fugitive', 'Other'];

const STATUS_OPTIONS = [
  { value: 'permitted', label: 'Permitted', color: '#0066FF', bg: '#EFF6FF' },
  { value: 'exempt', label: 'Exempt', color: '#D97706', bg: '#FFFBEB' },
  { value: 'voluntary', label: 'Voluntary Tracking', color: '#6B7280', bg: '#F8FAFC' },
];

const PARAMETERS = [
  { key: 'nox', label: 'NOx', unit: 'lbs' },
  { key: 'co', label: 'CO', unit: 'lbs' },
  { key: 'voc', label: 'VOC', unit: 'lbs' },
  { key: 'pm', label: 'PM', unit: 'lbs' },
  { key: 'pm10', label: 'PM10', unit: 'lbs' },
  { key: 'pm25', label: 'PM2.5', unit: 'lbs' },
  { key: 'so2', label: 'SO2', unit: 'lbs' },
  { key: 'hap', label: 'HAP (total)', unit: 'lbs' },
  { key: 'co2', label: 'CO2', unit: 'tons' },
  { key: 'operating_hours', label: 'Operating Hours', unit: 'hours' },
  { key: 'fuel_use', label: 'Fuel Use', unit: 'auto' },
];

const AVERAGING_PERIODS = [
  { value: 'rolling_12_month', label: 'Rolling 12-month' },
  { value: 'calendar_year', label: 'Calendar year' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'daily', label: 'Daily' },
  { value: 'hourly', label: 'Hourly' },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function ComplianceBar({ value, limit, unit }) {
  if (!limit || limit <= 0) return null;
  const pct = Math.min((value / limit) * 100, 100);
  const color = pct >= 100 ? '#DC2626' : pct >= 80 ? '#D97706' : '#16A34A';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginBottom: 3, fontFamily: 'var(--font-body)' }}>
        <span>{value.toFixed(1)} {unit}</span>
        <span>{pct.toFixed(0)}% of {limit} {unit} limit</span>
      </div>
      <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function Sources() {
  const [sources, setSources] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [filterFacility, setFilterFacility] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);
  const [emissionsData, setEmissionsData] = useState({});
  const [showEmissionsModal, setShowEmissionsModal] = useState(null);
  const [emissionsForm, setEmissionsForm] = useState({});

  const [formData, setFormData] = useState({
    facilityId: '', sourceId: '', name: '', description: '',
    sourceType: '', fuelType: '', fuelUnit: '', status: 'permitted',
  });
  const [limits, setLimits] = useState([]);
  const [newLimit, setNewLimit] = useState({ parameter: '', limitValue: '', limitUnit: 'lbs', averagingPeriod: 'rolling_12_month', permitCondition: '' });

  useEffect(() => { loadData(); }, [filterFacility]);

  const loadData = async () => {
    try {
      const [sourcesRes, facilitiesRes] = await Promise.all([
        api.get(`/sources${filterFacility ? `?facilityId=${filterFacility}` : ''}`),
        facilityAPI.getAll()
      ]);
      setSources(sourcesRes.data.sources || []);
      setFacilities(facilitiesRes.data.facilities || []);
    } catch (err) {
      toast.error('Failed to load sources');
    } finally {
      setLoading(false);
    }
  };

  const loadEmissions = async (sourceId) => {
    try {
      const res = await api.get(`/sources/${sourceId}/emissions`);
      setEmissionsData(prev => ({ ...prev, [sourceId]: res.data }));
    } catch (err) {}
  };

  const handleExpand = (sourceId) => {
    if (expandedSource === sourceId) {
      setExpandedSource(null);
    } else {
      setExpandedSource(sourceId);
      if (!emissionsData[sourceId]) loadEmissions(sourceId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSource) {
        await api.put(`/sources/${editingSource.id}`, { ...formData, limits });
        toast.success('Source updated!');
      } else {
        await api.post('/sources', { ...formData, limits });
        toast.success('Source created!');
      }
      setShowModal(false);
      setEditingSource(null);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save source');
    }
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setFormData({
      facilityId: source.facility_id, sourceId: source.source_id || '', name: source.name,
      description: source.description || '', sourceType: source.source_type || '',
      fuelType: source.fuel_type || '', fuelUnit: source.fuel_unit || '', status: source.status || 'permitted',
    });
    setLimits(source.limits?.map(l => ({
      parameter: l.parameter, limitValue: l.limit_value, limitUnit: l.limit_unit || 'lbs',
      averagingPeriod: l.averaging_period || 'rolling_12_month', permitCondition: l.permit_condition || ''
    })) || []);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this source?')) return;
    try {
      await api.delete(`/sources/${id}`);
      toast.success('Source deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const addLimit = () => {
    if (!newLimit.parameter || !newLimit.limitValue) return toast.error('Parameter and limit value required');
    setLimits([...limits, { ...newLimit }]);
    setNewLimit({ parameter: '', limitValue: '', limitUnit: 'lbs', averagingPeriod: 'rolling_12_month', permitCondition: '' });
  };

  const removeLimit = (i) => setLimits(limits.filter((_, idx) => idx !== i));

  const resetForm = () => {
    setFormData({ facilityId: '', sourceId: '', name: '', description: '', sourceType: '', fuelType: '', fuelUnit: '', status: 'permitted' });
    setLimits([]);
  };

  const openEmissionsModal = (source) => {
    const now = new Date();
    setEmissionsForm({ month: now.getMonth() + 1, year: now.getFullYear() });
    setShowEmissionsModal(source);
    if (!emissionsData[source.id]) loadEmissions(source.id);
  };

  const saveEmissions = async () => {
    try {
      await api.post(`/sources/${showEmissionsModal.id}/emissions`, {
        month: emissionsForm.month,
        year: emissionsForm.year,
        operatingHours: emissionsForm.operatingHours || null,
        fuelUse: emissionsForm.fuelUse || null,
        fuelType: showEmissionsModal.fuel_type,
        fuelUnit: showEmissionsModal.fuel_unit,
        noxLbs: emissionsForm.nox || null,
        coLbs: emissionsForm.co || null,
        vocLbs: emissionsForm.voc || null,
        pmLbs: emissionsForm.pm || null,
        pm10Lbs: emissionsForm.pm10 || null,
        pm25Lbs: emissionsForm.pm25 || null,
        so2Lbs: emissionsForm.so2 || null,
        hapLbs: emissionsForm.hap || null,
        co2Tons: emissionsForm.co2 || null,
        notes: emissionsForm.notes || null,
      });
      toast.success('Emissions log saved!');
      loadEmissions(showEmissionsModal.id);
      setShowEmissionsModal(null);
    } catch (err) {
      toast.error('Failed to save emissions');
    }
  };

  const getStatusStyle = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box' };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading sources...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <div className="page-title">Emission Sources</div>
          <div className="page-subtitle">Track emission units, permit limits, and monthly compliance data</div>
        </div>
        <button onClick={() => { setEditingSource(null); resetForm(); setShowModal(true); }} className="btn btn-primary">
          <Plus size={16} /> Add Source
        </button>
      </div>

      {/* Facility filter */}
      <div style={{ marginBottom: 20 }}>
        <select value={filterFacility} onChange={e => setFilterFacility(e.target.value)} style={{ maxWidth: 300 }}>
          <option value="">All Facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.name || f.facility_name}</option>)}
        </select>
      </div>

      {/* Sources list */}
      {sources.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <Factory size={40} color="#CBD5E1" style={{ margin: '0 auto 14px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628', marginBottom: 6 }}>No sources yet</div>
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>Add emission sources from your air permit to track compliance</div>
          <button onClick={() => setShowModal(true)} className="btn btn-primary"><Plus size={15} /> Add First Source</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sources.map(source => {
            const statusStyle = getStatusStyle(source.status);
            const emissions = emissionsData[source.id];
            const isExpanded = expandedSource === source.id;

            return (
              <div key={source.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Source header */}
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
                  onClick={() => handleExpand(source.id)}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Factory size={17} color="#0066FF" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                      {source.source_id && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{source.source_id}</span>
                      )}
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: '#0A1628' }}>{source.name}</span>
                      <span className="badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span>📍 {source.facility_name}</span>
                      {source.source_type && <span>🏭 {source.source_type}</span>}
                      {source.fuel_type && <span>⛽ {FUEL_TYPES.find(f => f.value === source.fuel_type)?.label || source.fuel_type}</span>}
                      {source.limits?.length > 0 && <span>📋 {source.limits.length} limit{source.limits.length !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={e => { e.stopPropagation(); openEmissionsModal(source); }} className="btn btn-ghost" style={{ fontSize: 14, padding: '6px 12px' }}>
                      <BarChart2 size={13} /> Log Emissions
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleEdit(source); }} className="icon-btn"><Edit2 size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(source.id); }} className="icon-btn" style={{ color: '#DC2626', borderColor: '#FECACA' }}><Trash2 size={14} /></button>
                    {isExpanded ? <ChevronDown size={16} color="#6B7280" /> : <ChevronRight size={16} color="#6B7280" />}
                  </div>
                </div>

                {/* Expanded: limits + rolling 12-month compliance */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #E2E8F0', padding: '12px 16px', background: '#FAFAFA' }}>
                    {source.limits?.length > 0 && (
                      <div>
                        <div className="section-label" style={{ marginBottom: 12 }}>Permit Limits vs Rolling 12-Month Actuals</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                          {source.limits.map((limit, i) => {
                            const rolling = emissions?.rolling12Totals;
                            const paramMap = {
                              nox: 'nox_lbs', co: 'co_lbs', voc: 'voc_lbs', pm: 'pm_lbs',
                              pm10: 'pm10_lbs', pm25: 'pm25_lbs', so2: 'so2_lbs', hap: 'hap_lbs',
                              co2: 'co2_tons', operating_hours: 'operating_hours', fuel_use: 'fuel_use'
                            };
                            const actualKey = paramMap[limit.parameter];
                            const actual = rolling && actualKey ? (parseFloat(rolling[actualKey]) || 0) : null;
                            const limitVal = parseFloat(limit.limit_value);
                            const pct = actual !== null && limitVal > 0 ? (actual / limitVal) * 100 : null;

                            return (
                              <div key={i} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                  <div>
                                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>{limit.parameter.toUpperCase().replace('_', ' ')}</span>
                                    {limit.permit_condition && <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 6 }}>§{limit.permit_condition}</span>}
                                  </div>
                                  {pct !== null && (
                                    pct >= 100 ? <AlertTriangle size={14} color="#DC2626" /> :
                                    pct >= 80 ? <AlertTriangle size={14} color="#D97706" /> :
                                    <CheckCircle size={14} color="#16A34A" />
                                  )}
                                </div>
                                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 6, fontFamily: 'var(--font-body)' }}>
                                  Limit: {limitVal} {limit.limit_unit} · {AVERAGING_PERIODS.find(p => p.value === limit.averaging_period)?.label || limit.averaging_period}
                                </div>
                                {actual !== null ? (
                                  <ComplianceBar value={actual} limit={limitVal} unit={limit.limit_unit} />
                                ) : (
                                  <div style={{ fontSize: 14, color: '#94a3b8', fontFamily: 'var(--font-body)', fontStyle: 'italic' }}>No emissions data logged yet</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Recent log entries */}
                    {emissions?.logs?.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div className="section-label" style={{ marginBottom: 10 }}>Recent Monthly Entries</div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, fontFamily: 'var(--font-body)' }}>
                            <thead>
                              <tr style={{ background: '#F8FAFC' }}>
                                {['Month', 'Op. Hours', 'Fuel Use', 'NOx (lbs)', 'CO (lbs)', 'VOC (lbs)', 'PM (lbs)', 'SO2 (lbs)'].map(h => (
                                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {emissions.logs.slice(0, 6).map((log, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                  <td style={{ padding: '7px 10px', fontWeight: 600, color: '#0A1628' }}>{MONTHS[log.month - 1]} {log.year}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.operating_hours || '—'}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.fuel_use ? `${log.fuel_use} ${log.fuel_unit || ''}` : '—'}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.nox_lbs || '—'}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.co_lbs || '—'}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.voc_lbs || '—'}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.pm_lbs || '—'}</td>
                                  <td style={{ padding: '7px 10px', color: '#374151' }}>{log.so2_lbs || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {source.description && (
                      <div style={{ marginTop: 12, fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{source.description}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Source Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>{editingSource ? 'Edit Source' : 'Add Emission Source'}</div>
              <button onClick={() => { setShowModal(false); setEditingSource(null); }} className="icon-btn"><X size={16} /></button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '14px 18px' }}>
              {/* Basic info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Facility *</label>
                  <select required value={formData.facilityId} onChange={e => setFormData({...formData, facilityId: e.target.value})} style={inputStyle}>
                    <option value="">Select facility...</option>
                    {facilities.map(f => <option key={f.id} value={f.id}>{f.name || f.facility_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Source ID (from permit)</label>
                  <input type="text" value={formData.sourceId} onChange={e => setFormData({...formData, sourceId: e.target.value})} placeholder="e.g. EU-001, BOILER-1" style={inputStyle} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Source Name *</label>
                <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Boiler #1, Emergency Generator" style={inputStyle} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="e.g. 10 MMBtu/hr natural gas fired boiler" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Source Type</label>
                  <select value={formData.sourceType} onChange={e => setFormData({...formData, sourceType: e.target.value})} style={inputStyle}>
                    <option value="">Select type...</option>
                    {SOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Fuel / Process Type</label>
                  <select value={formData.fuelType} onChange={e => {
                    const fuel = FUEL_TYPES.find(f => f.value === e.target.value);
                    setFormData({...formData, fuelType: e.target.value, fuelUnit: fuel?.unit || ''});
                  }} style={inputStyle}>
                    <option value="">Select fuel...</option>
                    {FUEL_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Permit Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Limits section */}
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '11px 14px', marginBottom: 20 }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>📋 Permit Limits</div>

                {limits.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    {limits.map((limit, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'white', border: '1px solid #E2E8F0', borderRadius: 7, marginBottom: 6, fontSize: 14, fontFamily: 'var(--font-body)' }}>
                        <span style={{ flex: 1, color: '#0A1628', fontWeight: 600 }}>{limit.parameter.toUpperCase().replace('_', ' ')}</span>
                        <span style={{ color: '#374151' }}>{limit.limitValue} {limit.limitUnit}</span>
                        <span style={{ color: '#6B7280' }}>· {AVERAGING_PERIODS.find(p => p.value === limit.averagingPeriod)?.label}</span>
                        {limit.permitCondition && <span style={{ color: '#6B7280' }}>§{limit.permitCondition}</span>}
                        <button type="button" onClick={() => removeLimit(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 2 }}><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add limit row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#6B7280', marginBottom: 3, fontFamily: 'var(--font-body)' }}>Parameter</label>
                    <select value={newLimit.parameter} onChange={e => {
                      const param = PARAMETERS.find(p => p.key === e.target.value);
                      setNewLimit({...newLimit, parameter: e.target.value, limitUnit: param?.unit === 'auto' ? (formData.fuelUnit || 'units') : (param?.unit || 'lbs')});
                    }} style={{ ...inputStyle, fontSize: 12 }}>
                      <option value="">Select...</option>
                      {PARAMETERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#6B7280', marginBottom: 3, fontFamily: 'var(--font-body)' }}>Limit Value</label>
                    <input type="number" value={newLimit.limitValue} onChange={e => setNewLimit({...newLimit, limitValue: e.target.value})} placeholder="e.g. 500" style={{ ...inputStyle, fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#6B7280', marginBottom: 3, fontFamily: 'var(--font-body)' }}>Unit</label>
                    <input type="text" value={newLimit.limitUnit} onChange={e => setNewLimit({...newLimit, limitUnit: e.target.value})} placeholder="lbs, tons, hrs" style={{ ...inputStyle, fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#6B7280', marginBottom: 3, fontFamily: 'var(--font-body)' }}>Period</label>
                    <select value={newLimit.averagingPeriod} onChange={e => setNewLimit({...newLimit, averagingPeriod: e.target.value})} style={{ ...inputStyle, fontSize: 12 }}>
                      {AVERAGING_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#6B7280', marginBottom: 3, fontFamily: 'var(--font-body)' }}>Permit §</label>
                    <input type="text" value={newLimit.permitCondition} onChange={e => setNewLimit({...newLimit, permitCondition: e.target.value})} placeholder="5.2.1" style={{ ...inputStyle, fontSize: 12 }} />
                  </div>
                  <button type="button" onClick={addLimit} className="btn btn-primary" style={{ padding: '8px 12px', fontSize: 14, whiteSpace: 'nowrap' }}>+ Add</button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => { setShowModal(false); setEditingSource(null); }} className="btn btn-ghost">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingSource ? 'Update' : 'Create'} Source</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Emissions Log Modal */}
      {showEmissionsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>Log Monthly Emissions</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#6B7280', marginTop: 2 }}>{showEmissionsModal.name}</div>
              </div>
              <button onClick={() => setShowEmissionsModal(null)} className="icon-btn"><X size={16} /></button>
            </div>

            <div style={{ padding: '14px 18px' }}>
              {/* Month/Year selector */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div>
                  <label className="form-label">Month</label>
                  <select value={emissionsForm.month} onChange={e => setEmissionsForm({...emissionsForm, month: parseInt(e.target.value)})} style={inputStyle}>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Year</label>
                  <select value={emissionsForm.year} onChange={e => setEmissionsForm({...emissionsForm, year: parseInt(e.target.value)})} style={inputStyle}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* Operating hours + fuel */}
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '11px 14px', marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: '#1E40AF', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Operational Data</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4, fontFamily: 'var(--font-body)', fontWeight: 600 }}>Operating Hours</label>
                    <input type="number" step="0.1" value={emissionsForm.operatingHours || ''} onChange={e => setEmissionsForm({...emissionsForm, operatingHours: e.target.value})} placeholder="e.g. 720" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4, fontFamily: 'var(--font-body)', fontWeight: 600 }}>
                      Fuel Use {showEmissionsModal.fuel_unit ? `(${showEmissionsModal.fuel_unit})` : ''}
                    </label>
                    <input type="number" step="0.01" value={emissionsForm.fuelUse || ''} onChange={e => setEmissionsForm({...emissionsForm, fuelUse: e.target.value})} placeholder="e.g. 5000" style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Emissions by pollutant */}
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '11px 14px', marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Emissions (lbs unless noted)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  {[
                    { key: 'nox', label: 'NOx (lbs)' },
                    { key: 'co', label: 'CO (lbs)' },
                    { key: 'voc', label: 'VOC (lbs)' },
                    { key: 'pm', label: 'PM (lbs)' },
                    { key: 'pm10', label: 'PM10 (lbs)' },
                    { key: 'pm25', label: 'PM2.5 (lbs)' },
                    { key: 'so2', label: 'SO2 (lbs)' },
                    { key: 'hap', label: 'HAP total (lbs)' },
                    { key: 'co2', label: 'CO2 (tons)' },
                  ].map(p => (
                    <div key={p.key}>
                      <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4, fontFamily: 'var(--font-body)', fontWeight: 600 }}>{p.label}</label>
                      <input type="number" step="0.01" value={emissionsForm[p.key] || ''} onChange={e => setEmissionsForm({...emissionsForm, [p.key]: e.target.value})} placeholder="0.00" style={inputStyle} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: 20 }}>
                <label className="form-label">Notes / Deviations</label>
                <textarea value={emissionsForm.notes || ''} onChange={e => setEmissionsForm({...emissionsForm, notes: e.target.value})} placeholder="Any deviations, shutdowns, or notes for this month..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setShowEmissionsModal(null)} className="btn btn-ghost">Cancel</button>
                <button onClick={saveEmissions} className="btn btn-primary">Save Emissions Log</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Sources;
